import React, { useState } from 'react';
import { forgotPassword, terminateAccount } from '../../../modules/users';
import { useNotification } from '../../../hooks/NotificationContext';
import ActionMenu from '../../../ui/primitives/ActionMenu';
import ConfirmationModal from '../../../ui/primitives/ConfirmationModal';
import { resendLinkToUser } from '../../../lib/utils';
import { modifyCustomerStatus, upgradeToFreemium } from '../../../modules/admin';
import { createExportRequest } from '../../../modules/project';
import { IconButton, Tooltip, Typography } from '@mui/material';
import {
  FileDownload,
  InfoOutlined,
  LockReset,
  Block,
  CheckCircleOutline,
  Diamond,
  CloudDownload,
  Settings,
  Undo,
  MarkEmailRead,
  DeleteForever,
} from '@mui/icons-material';

const CustomerTableActions = ({ row, fetchCustomerList, latestExport = null, updateExportRequest }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();
  const { organization_id: organizationId, customer } = row;

  const user = row?.org_admin || {};
  const { status, platform, full_name, email, id } = user;

  const handleOpenConfirm = (action) => {
    setConfirmAction(action);
    setShowConfirm(true);
  };

  const handleCloseConfirm = () => setShowConfirm(false);

  const handleExportOrganization = async () => {
    const payload = {
      projects: 'all',
      organizationId: organizationId,
      options: ['testcases'],
      existingRequest: null,
      exportType: 'new'
    };

    try {
      await createExportRequest(payload);
      if (updateExportRequest) {
        await updateExportRequest();
      }
      addNotification('success', 'Export request created successfully. You will receive an email when the export is ready.');
    } catch (error) {
      addNotification('error', error?.message || 'Failed to create export request');
    }
  };

  const handleDownloadExport = () => {
    if (latestExport && latestExport.status === 'completed' && latestExport.export_link) {
      const link = document.createElement('a');
      link.href = latestExport.export_link;
      link.download = latestExport.export_key || 'export.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      addNotification('success', 'Download started');
    } else {
      addNotification('error', 'Export file not available');
    }
  };

  const handleSubmit = async () => {
    if (!confirmAction) return;

    setLoading(true);
    try {
      let response;

      if (confirmAction === 'resetPassword') {
        await forgotPassword({ email });
        addNotification('success', 'Password reset link sent successfully!');
      } else if (confirmAction === 'resendConfirmationEmail') {
        await handleResendLink(email);
      } else if (confirmAction === 'exportCustomerData') {
        await handleExportOrganization();
      } else if (confirmAction === 'upgradeToFreemium') {
        response = await upgradeToFreemium(organizationId);
        fetchCustomerList();
        addNotification('success', response?.message || 'Organization upgraded to Freemium successfully!');
      } else if (confirmAction === 'permanentDeleteCustomer') {
        response = await terminateAccount(organizationId);
        fetchCustomerList();
        addNotification(
          'success',
          response?.message || 'Customer permanently deleted successfully!'
        );
      } else {
        response = await modifyCustomerStatus({
          organizationId,
          type: confirmAction,
        });
        fetchCustomerList();
        addNotification(
          'success',
          response?.message || 'Customer updated successfully!'
        );
      }

    } catch (err) {
      addNotification('danger', err.message);
    } finally {
      setLoading(false);
      handleCloseConfirm();
    }
  };

  const hasRequestedExport = latestExport &&
    (latestExport.status === 'requested' || latestExport.status === 'processing');

  const hasCompletedExport = latestExport &&
    latestExport.status === 'completed' &&
    latestExport.export_link;

  const resetPasswordConfirmationBody =
    platform === 'web'
      ? `Are you sure you want to reset the password for ${full_name}? 
        A link will be sent to their registered email to reset their password.`
      : `This user signed up using an external authentication method and cannot reset their password manually.`;

  const exportConfirmationBody = (
    <>
      <Typography variant="body2" gutterBottom>
        Are you sure you want to export all data for <strong>{customer}</strong>?
      </Typography>

      <Typography variant="body2" sx={{ mt: 1 }}>
        This export will include:
      </Typography>

      <ul style={{ marginTop: 6, marginBottom: 6, paddingLeft: 18 }}>
        <li>All projects in this organization</li>
        <li>All test cases, including test steps and expected results</li>
        <li>All related data associated with these projects</li>
      </ul>

      <Typography variant="body2">
        This process may take some time to complete. We will notify you once the export is ready. The exported data will be available for download for 7 days.
      </Typography>
    </>
  );

  // ActionMenu item config — hidden replaces the old "visible" filtering
  const menuItems = [
    {
      label: 'Reset Admin Password',
      icon: <LockReset />,
      hidden: status !== 'active',
      onClick: () => handleOpenConfirm('resetPassword'),
    },
    {
      label: 'Deactivate Customer',
      icon: <Block />,
      color: 'warning',
      hidden: !(status === 'active' || status === 'pending'),
      onClick: () => handleOpenConfirm('deactivate'),
    },
    {
      label: 'Activate Customer',
      icon: <CheckCircleOutline />,
      color: 'success',
      hidden: !(status === 'inactive' || status === 'suspended'),
      onClick: () => handleOpenConfirm('activate'),
    },
    {
      label: 'Upgrade to Freemium',
      icon: <Diamond />,
      hidden: !(status === 'active' && row.type === 'Free'),
      onClick: () => handleOpenConfirm('upgradeToFreemium'),
    },
    {
      label: hasRequestedExport ? 'Export In Progress...' : 'Export Customer Data',
      icon: <CloudDownload />,
      hidden: status === 'pending',
      disabled: !!hasRequestedExport,
      onClick: () => handleOpenConfirm('exportCustomerData'),
    },
    {
      label: 'Manage Subscription',
      icon: <Settings />,
      hidden: status === 'delete-requested',
      disabled: true,
      onClick: () => (window.location.href = `/subscriptions/${id}`),
    },
    {
      label: 'Cancel Deletion',
      icon: <Undo />,
      hidden: status !== 'delete-requested',
      onClick: () => handleOpenConfirm('cancelDeletion'),
    },
    {
      label: 'Resend Confirmation Email',
      icon: <MarkEmailRead />,
      hidden: status !== 'pending',
      onClick: () => handleOpenConfirm('resendConfirmationEmail'),
    },
    {
      label: 'Permanently Delete Customer',
      icon: <DeleteForever />,
      color: 'error',
      hidden: status !== 'inactive',
      onClick: () => handleOpenConfirm('permanentDeleteCustomer'),
    },
  ];

  const confirmationMessages = {
    resetPassword: resetPasswordConfirmationBody,
    activate: `Are you sure you want to activate ${customer}? This will allow the organization's users to access the platform again.`,
    deactivate: `Are you sure you want to deactivate ${customer}? This will prevent the organization's users from accessing the platform. You can reactivate them at any time.`,
    upgradeToFreemium: `Are you sure you want to upgrade ${customer} to Freemium? This will allow the organization to add multiple projects with multiple users.`,
    cancelDeletion: `Are you sure you want to cancel the account deletion request for ${customer}? This will re-activate the customer and allow them to access the platform again?`,
    resendConfirmationEmail: `The org admin has not verified their email. Are you sure you want to resend the confirmation email to ${full_name}?`,
    exportCustomerData: exportConfirmationBody,
    permanentDeleteCustomer: `Are you absolutely sure you want to permanently delete "${customer}"? 
    This action cannot be undone. All organization data, projects, users, and related records will be permanently removed from the system.`,
  };

  const handleResendLink = async (userEmail) => {
    try {
      const response = await resendLinkToUser(userEmail, 'confirmation');
      addNotification('success', response);
    } catch (error) {
      addNotification('error', error.message);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' }}>

      {hasRequestedExport && (
        <Tooltip title="Request received. Preparing export." arrow>
          <InfoOutlined sx={{ color: '#1976d2', fontSize: '20px' }} />
        </Tooltip>
      )}
      {hasCompletedExport && (
        <Tooltip title="Your export is ready. Available for download for 7 days." arrow>
          <IconButton
            size="small"
            onClick={handleDownloadExport}
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            }}
          >
            <FileDownload sx={{ fontSize: '20px' }} />
          </IconButton>
        </Tooltip>
      )}

      <ActionMenu items={menuItems} tooltip="Customer actions" />

      <ConfirmationModal
        show={showConfirm}
        handleClose={handleCloseConfirm}
        handleConfirm={handleSubmit}
        title={
          confirmAction
            ? confirmAction
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase())
              .trim()
            : ''
        }
        body={confirmationMessages[confirmAction] || ''}
        buttonLabel={loading ? 'Please wait...' : 'Confirm'}
        buttonLabelSecondary="Cancel"
        buttonStyle={confirmAction === 'permanentDeleteCustomer' ? 'error' : 'primary'}
        loading={loading}
        hideConfirmButton={confirmAction === 'resetPassword' && platform !== 'web'}
      />
    </div>
  );
};

export default CustomerTableActions;
